import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Paper,
  Box,
  tableCellClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { CustomTableBodyLoading } from "components/table/CustomTableBodyLoading";
import { useMemo, useState } from "react";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "none !important",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  "& .MuiPaper-root": {
    boxShadow: "none",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  wordBreak: "break-word",
  [`&.${tableCellClasses.head}`]: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.04)"
        : theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.85em",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
  },
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  border: `1px solid ${theme.palette.error.light}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "rgba(211, 47, 47, 0.04)",
  textAlign: "center",
  color: theme.palette.error.main,
}));

export function DataTable({
  columns = [],
  data = [],
  isLoading = false,
  isError = false,
  noDataMessage = "No data available.",
  onSelectionChange,
  onRowClick,
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [rowSelection, setRowSelection] = useState({});

  const handleSort = (accessorKey) => {
    if (!accessorKey) return;
    setSortConfig((prev) => ({
      key: accessorKey,
      direction:
        prev.key === accessorKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const handleRowSelection = (rowIndex) => {
    const updated = {
      ...rowSelection,
      [rowIndex]: !rowSelection[rowIndex],
    };
    // remove key if deselected
    if (!updated[rowIndex]) delete updated[rowIndex];

    setRowSelection(updated);

    if (typeof onSelectionChange === "function") {
      const selectedRows = Object.keys(updated)
        .map((key) => data[Number(key)])
        .filter(Boolean);
      onSelectionChange(selectedRows);
    }
  };

  const getCellValue = (col, row, rowIndex) => {
    if (typeof col.cell === "function") {
      return col.cell({
        row: {
          original: row,
          index: rowIndex,
          getValue: (key) => row[key],
        },
      });
    }
    return col.accessorKey ? (row[col.accessorKey] ?? "N/A") : null;
  };

  const getHeaderValue = (col) => {
    if (typeof col.header === "function") {
      return col.header();
    }
    return col.header ?? "";
  };

  if (isError) {
    return (
      <ErrorBox>
        <p style={{ fontWeight: 500, fontSize: "0.9rem" }}>
          Something went wrong
        </p>
        <p style={{ fontSize: "0.8rem", opacity: 0.7 }}>
          We couldn&apos;t load this data. Please try again later.
        </p>
      </ErrorBox>
    );
  }

  return (
    <StyledTableContainer component={Paper}>
      <Table aria-label="data table">
        <TableHead>
          <TableRow>
            {columns.map((col) => {
              const key = col.accessorKey || col.id;
              const isSorted = sortConfig.key === col.accessorKey;
              return (
                <StyledTableCell key={key} variant="head">
                  {col.accessorKey ? (
                    <TableSortLabel
                      active={isSorted}
                      direction={isSorted ? sortConfig.direction : "asc"}
                      onClick={() => handleSort(col.accessorKey)}
                    >
                      {getHeaderValue(col)}
                    </TableSortLabel>
                  ) : (
                    getHeaderValue(col)
                  )}
                </StyledTableCell>
              );
            })}
          </TableRow>
        </TableHead>

        {isLoading ? (
          <CustomTableBodyLoading columns={columns.length} />
        ) : (
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((row, rowIndex) => (
                <StyledTableRow
                  key={rowIndex}
                  selected={Boolean(rowSelection[rowIndex])}
                  onClick={() => {
                    handleRowSelection(rowIndex);
                    if (typeof onRowClick === "function") onRowClick(row);
                  }}
                >
                  {columns.map((col) => {
                    const key = col.accessorKey || col.id;
                    return (
                      <StyledTableCell key={key}>
                        {getCellValue(col, row, rowIndex)}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ height: "200px" }}
                >
                  {noDataMessage}
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    </StyledTableContainer>
  );
}
